import {useQuery} from '@apollo/client';
import {Alert, Button, Pagination} from 'antd';
import {ReloadOutlined} from '@ant-design/icons';
import {Section} from 'pages/home/shared/DashboardUI';
import {GET_MY_SUPPORT_PERFORMANCE, GET_MY_SUPPORT_TICKETS} from './resolvers';
import {usePerformanceFilters} from './usePerformanceFilters';
import {PerformanceFilterBar} from './PerformanceFilterBar';
import {MetricCards} from './MetricCards';
import {RankingTable} from './RankingTable';
import {TrendCharts} from './TrendCharts';
import {TicketsSection} from './TicketsSection';
import {TicketList} from './TicketList';
import {Benchmarks} from './Benchmarks';
import {BucketToggle, TRENDS_TERM} from './AgentPerformanceDetail';
import {
  DETAIL_PAGE_SIZE,
  MY_DEFAULT_TAB,
  describeRange,
  myFeedbackInput,
  myInput,
} from './performanceUtils';
import {Explained, TERMS} from './Info';
import styles from './performance.module.scss';

const NO_ACCESS = /permission/i;

const errorFor = (result, error, fallback) => {
  if (result?.__typename === 'Error') return result.message;
  if (error && !result) {
    return NO_ACCESS.test(error.message || '')
      ? "You don't have access to personal support numbers."
      : fallback;
  }
  return null;
};

/**
 * The signed-in support agent's own report. The server works out who that is
 * from the session; nothing here names, lists or picks another agent, and the
 * team comparison is scalar only.
 *
 * Two requests: the report (with the pending list, oldest first by default),
 * and the feedback list, so each list pages on its own.
 */
export const MyPerformance = () => {
  const {filters, update} = usePerformanceFilters();
  const report = useQuery(GET_MY_SUPPORT_PERFORMANCE, {
    variables: {input: myInput(filters)},
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const feedback = useQuery(GET_MY_SUPPORT_TICKETS, {
    variables: {input: myFeedbackInput(filters)},
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const result = report.data?.getMySupportPerformance;
  const data =
    result?.__typename === 'ResponseWithMySupportPerformance'
      ? result.data
      : null;
  const errorMessage = errorFor(
    result,
    report.error,
    "Couldn't load your performance.",
  );
  const feedbackResult = feedback.data?.getMySupportPerformance;
  const feedbackData =
    feedbackResult?.__typename === 'ResponseWithMySupportPerformance'
      ? feedbackResult.data
      : null;
  const feedbackError = errorFor(
    feedbackResult,
    feedback.error,
    "Couldn't load your feedback.",
  );

  const refresh = () => {
    report.refetch();
    feedback.refetch();
  };

  return (
    <div className={styles.page}>
      <div className={styles.detailHead}>
        <span className={styles.eyebrow}>Your numbers</span>
        <h2 className={styles.detailTitle}>My performance</h2>
        <p className={styles.detailNote}>
          Your ratings, speed and tickets, with the team&apos;s averages for
          comparison.
        </p>
      </div>

      <PerformanceFilterBar filters={filters} update={update} />
      <div className={styles.scopeRow}>
        <p className={styles.scope}>
          {describeRange(filters.from, filters.to)} · Pending now is live
        </p>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          loading={report.loading && Boolean(data)}
          onClick={refresh}>
          Refresh
        </Button>
      </div>

      {errorMessage && (
        <Alert
          type="error"
          showIcon
          message={errorMessage}
          action={
            <Button size="small" onClick={refresh}>
              Retry
            </Button>
          }
        />
      )}

      <MetricCards
        metrics={data?.metrics}
        firstResponse={data?.firstResponse ?? null}
        pendingAsOf={data?.pendingAsOf}
        pendingTerm={TERMS.agentPending}
        loading={report.loading && !data}
      />

      {data?.benchmarks && (
        <Section
          title="Against the team"
          description="Same dates and filters. Team figures are worked out by the server across every support agent.">
          <Benchmarks metrics={data.metrics} benchmarks={data.benchmarks} />
        </Section>
      )}

      <TicketsSection
        title="My tickets"
        filters={filters}
        update={update}
        defaultTab={MY_DEFAULT_TAB}
        tickets={data?.tickets}
        pagination={data?.pagination}
        loading={report.loading}
      />

      <Section
        title="Customer feedback"
        description="Your resolved tickets in the period that the customer rated, most recently resolved first.">
        {feedbackError ? (
          <Alert type="error" showIcon message={feedbackError} />
        ) : (
          <>
            <TicketList
              tickets={feedbackData?.tickets}
              loading={feedback.loading}
              emptyText="No rated tickets in this period"
              showComments
            />
            {feedbackData?.pagination?.total > 0 && (
              <Pagination
                className={styles.pager}
                current={filters.feedbackPage}
                pageSize={DETAIL_PAGE_SIZE}
                total={feedbackData.pagination.total}
                showSizeChanger={false}
                onChange={feedbackPage => update({feedbackPage})}
                showTotal={total => `${total} rated tickets`}
              />
            )}
          </>
        )}
      </Section>

      <Section
        title={<Explained text={TRENDS_TERM}>Trends</Explained>}
        actions={<BucketToggle value={filters.bucket} update={update} />}>
        <TrendCharts
          trends={data?.trends}
          bucket={filters.bucket}
          loading={report.loading}
          metrics={data?.metrics}
        />
      </Section>

      <div className={styles.twoColumns}>
        <Section title="Top categories handled">
          <RankingTable
            rows={data?.categories}
            basis="RESOLVED"
            loading={report.loading}
          />
        </Section>
        <Section title="Top issues handled">
          <RankingTable
            rows={data?.issues}
            basis="RESOLVED"
            loading={report.loading}
            showAncestry
          />
        </Section>
      </div>
    </div>
  );
};
